<template lang="pug">
.purchase-invoice-import

  section
    el-header
      el-col(:span="24")
        el-page-header(@back="back" content="Importar notas de entrada")

    el-card(shadow="never")
      p
        | Importe o XML de cada nota ao recebê-la. Antes de fechar o mês, importe também o ZIP do período baixado do Qive,
        | sem descompactar: as pastas do ZIP indicam notas canceladas ou desconhecidas depois do download e ele completa
        | notas que tenham ficado de fora. Importar o mesmo arquivo de novo não duplica a nota.

      input(ref="fileInput" type="file" multiple accept=".zip,.xml" style="display: none" @change="readSelection")

      el-row(type="flex" align="middle" :gutter="16")
        el-col(:span="6")
          el-button.stretched(icon="el-icon-folder-opened" :loading="reading" :disabled="importing" @click="selectFiles")
            | Selecionar arquivos
        el-col(:span="12")
          span(v-if="files.length") {{ files.length }} arquivos XML prontos para importar
        el-col(:span="6")
          el-button.stretched(type="primary" icon="el-icon-upload2" :disabled="!files.length || importing" :loading="importing" @click="startImport")
            | Importar

      el-progress.progress(v-if="total" :percentage="progress")

    el-card.results(v-if="results.length" shadow="never")
      .summary
        el-tag(v-for="item in summary" :key="item.outcome" :type="tagType(item.outcome)") {{ outcomeLabels[item.outcome] }}: {{ item.count }}

      el-table(:data="sortedResults" size="mini" max-height="520")
        el-table-column(prop="path" label="Arquivo" min-width="320" show-overflow-tooltip)
        el-table-column(label="Resultado" width="190")
          template(slot-scope="scope")
            el-tag(size="mini" :type="tagType(scope.row.outcome)") {{ outcomeLabels[scope.row.outcome] }}
        el-table-column(prop="message" label="Detalhe" min-width="240" show-overflow-tooltip)
        el-table-column(width="70" fixed="right")
          template(slot-scope="scope")
            router-link(v-if="scope.row.purchaseInvoiceId" :to="{ name: 'purchase-invoice-detail', params: { id: scope.row.purchaseInvoiceId } }") Ver

</template>
<script setup>
import JSZip from 'jszip'
import { computed, ref } from 'vue'
import { useRouter } from 'vue2-helpers/vue-router'

import PurchaseInvoiceService from '@/services/fiscal/PurchaseInvoiceService'
import { useMessage } from '@/utils/Message'
import { outcomeLabels } from './labels'

// The API accepts up to 100 files per request; batches also stay small enough for proxies' body size limits.
const MAX_FILES_PER_BATCH = 50
const MAX_BATCH_CHARACTERS = 900 * 1024

const OUTCOME_ORDER = ['REJECTED', 'UPDATED', 'IMPORTED', 'EVENT_RECORDED', 'ALREADY_IMPORTED', 'SKIPPED_OWN_INVOICE']

const router = useRouter()
const message = useMessage()

const fileInput = ref()
const files = ref([])
const results = ref([])
const reading = ref(false)
const importing = ref(false)
const total = ref(0)
const processed = ref(0)

const progress = computed(() => (total.value ? Math.round((processed.value / total.value) * 100) : 0))

const summary = computed(() => OUTCOME_ORDER
  .map(outcome => ({ outcome, count: results.value.filter(it => it.outcome === outcome).length }))
  .filter(it => it.count > 0))

const sortedResults = computed(() => [...results.value]
  .sort((a, b) => OUTCOME_ORDER.indexOf(a.outcome) - OUTCOME_ORDER.indexOf(b.outcome)))

const tagType = (outcome) => {
  switch (outcome) {
    case 'REJECTED': return 'danger'
    case 'UPDATED': return 'warning'
    case 'IMPORTED':
    case 'EVENT_RECORDED': return 'success'
    default: return 'info'
  }
}

const back = () => router.push({ name: 'purchase-invoice-list' })

const selectFiles = () => fileInput.value.click()

const readZip = async (file) => {
  const zip = await JSZip.loadAsync(file)
  const entries = Object.values(zip.files).filter(entry => !entry.dir && entry.name.toLowerCase().endsWith('.xml'))

  return Promise.all(entries.map(async entry => ({ path: entry.name, content: await entry.async('string') })))
}

const readSelection = async (event) => {
  const selected = Array.from(event.target.files || [])

  reading.value = true
  results.value = []
  total.value = 0
  processed.value = 0

  try {
    const collected = []

    for (const file of selected) {
      const name = file.name.toLowerCase()

      if (name.endsWith('.zip')) {
        collected.push(...await readZip(file))
      } else if (name.endsWith('.xml')) {
        collected.push({ path: file.webkitRelativePath || file.name, content: await file.text() })
      }
    }

    files.value = collected

    if (!collected.length) {
      message.warning('Nenhum arquivo XML encontrado na seleção.')
    }
  } catch (error) {
    message.error(`Não foi possível ler os arquivos: ${error.message}`)
  } finally {
    reading.value = false
    event.target.value = ''
  }
}

const toBatches = (items) => {
  const batches = []
  let current = []
  let characters = 0

  for (const item of items) {
    const size = item.content.length + item.path.length

    if (current.length && (current.length >= MAX_FILES_PER_BATCH || characters + size > MAX_BATCH_CHARACTERS)) {
      batches.push(current)
      current = []
      characters = 0
    }

    current.push(item)
    characters += size
  }

  if (current.length) {
    batches.push(current)
  }

  return batches
}

const startImport = async () => {
  importing.value = true
  results.value = []
  total.value = files.value.length
  processed.value = 0

  for (const batch of toBatches(files.value)) {
    try {
      const response = await PurchaseInvoiceService.import(batch)

      results.value.push(...response.results)
    } catch (error) {
      const reason = error.description || error.message
      results.value.push(...batch.map(file => ({ path: file.path, outcome: 'REJECTED', message: reason })))
    }

    processed.value += batch.length
  }

  files.value = []
  importing.value = false
}
</script>
<style lang="scss" scoped>
.progress {
  margin-top: 16px;
}

.results {
  margin-top: 16px;
}

.summary {
  margin-bottom: 12px;

  .el-tag {
    margin-right: 8px;
  }
}
</style>
